/**
 * Browser mirror of api/_materialText.js — PREFILL ONLY.
 *
 * When a tech saves a quote line into the price book, the save panel prefills a
 * cleaned product name so they are not editing prose by hand. The SERVER is the
 * authority: api/pricebook.js upsertQuotePrice runs the real sanitizer over
 * whatever arrives and rejects what still reads as a quote line, so drift
 * between this file and the server one is cosmetic, not a hole. (It used to be
 * a hole: this logic lived in two copies inside components, was the ONLY guard,
 * and the endpoint is crew-writable — so a direct POST walked straight past it.
 * 32 price rows and 13 products in the live book came in that way.)
 *
 * Kept deliberately small. CRA cannot import from api/, so a full copy would be
 * a second implementation to keep in step; this only has to produce a sensible
 * starting value in a text box the tech can still edit.
 */

// Rooms and areas — NOT product nouns. The previous version of this list
// included "main", "panel", "meter", "service", "feed" and "run", which are the
// actual names of things ("200A meter main", "main breaker loadcentre"), so it
// truncated real product names.
const SITE_WORDS = "bedroom|bathroom|ensuite|washroom|kitchen|living room|dining|hallway|foyer|mudroom|pantry|closet|den|nursery|office|studio|suite|basement|crawlspace|attic|loft|garage|carport|shop|workshop|shed|barn|cabin|laundry|deck|patio|porch|driveway|yard|dock|greenhouse|sauna|upstairs|downstairs";
// A span carrying any of these is product identity and must never be cut.
const IDENTITY = /\d+\s*\/?\s*\d*\s*(?:a|amp|amps|v|kv|w|kw|kva|p|pole|awg|kcmil|mcm|ga|cct|cir|circuit|in|inch|ft|feet|mm|m)\b|\b\d{1,2}-\d{2}\s?[rp]?\b|\b\d{1,3}\s*\/\s*\d{1,3}\b|\b(?=[A-Z0-9-]*[A-Z])(?=[A-Z0-9-]*\d)[A-Z][A-Z0-9-]{2,}\b/;

export function cleanCatalogDescription(desc) {
  const raw = String(desc || "").trim();
  if (!raw) return raw;
  let d = raw;
  // Trailing quoted purpose — '… panel "for bedroom suite"'.
  d = d.replace(/\s*["“'‘]\s*(?:for|to|serving|feeding|in|at|on)\b[^"”'’]*["”'’]\s*$/i, (m) => (IDENTITY.test(m) ? m : " "));
  // Trailing parenthetical that names a place rather than a spec.
  d = d.replace(/\s*\(([^()]*)\)\s*$/, (m, inner) =>
    (new RegExp(`\\b(?:${SITE_WORDS})\\b`, "i").test(inner) && !IDENTITY.test(inner) ? " " : m));
  // Em-dash tail, dropped only when the head still carries identity.
  const em = d.split(/\s*[—–]\s*/);
  if (em.length === 2 && !IDENTITY.test(em[1]) && em[0].split(/\s+/).filter(Boolean).length >= 3) d = em[0];
  // "… , for the basement" / "… , room 209 oven circuit".
  d = d.replace(new RegExp(`[,;]\\s*(?:for|to|serving|feeding)?\\s*(?:the\\s+)?(?:${SITE_WORDS}|room\\s+\\d+)\\b[^,;]*$`, "i"), (m) => (IDENTITY.test(m) ? m : ""));
  d = d.replace(new RegExp(`\\s+(?:for|to|in|at|on)\\s+(?:the\\s+)?(?:${SITE_WORDS})\\b[^,;]*$`, "i"), (m) => (IDENTITY.test(m) ? m : ""));
  // Estimator hedges.
  d = d.replace(/[,;]?\s*(?:size\s+tbd|tbd(?:\s+on\s+site)?|to be confirmed|as\s+required|as\s+needed|if\s+required|allowance(?:\s+only)?|approx\.?)\s*$/i, "");
  d = d.replace(/\$\s?\d[\d,]*(?:\.\d+)?/g, " ");
  d = d.replace(/\s{2,}/g, " ").replace(/^[\s,;:—–-]+|[\s,;:—–-]+$/g, "").trim();
  // Never hand back something shorter than a product name.
  return d.split(/\s+/).filter(Boolean).length >= 2 ? d : raw;
}

export default cleanCatalogDescription;
